<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from "vue";
  import noDataImage from "~/assets/images/empty/no-data.png";
  import AppEvent from "~/utils/app-event";
  import Logger from "~/utils/logger";

  // 获取路由参数
  const route = useRoute();

  // 响应式数据
  const id = ref<string>("");
  const title = ref<string>("");
  const background = ref<string>("");
  const content = ref<string>("");
  const contentRef = ref<HTMLDivElement | null>(null);
  const visible = ref<boolean>(false);
  const empty = ref<boolean>(false);

  const noDataImageUrl = noDataImage;
  const api = useApi();

  // 计算属性
  const style = computed(() => ({
    background: `${background.value}`,
  }));

  // SEO 设置
  useHead({
    titleTemplate: title.value ? `${title.value} - 资讯动态 - %s` : `资讯动态 - %s`,
    meta: [
      {
        name: "viewport",
        content: "width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no",
      },
    ],
  });

  // 加载数据
  const loadData = async (): Promise<void> => {
    if (!id.value) return;

    const params: Record<string, any> = { id: id.value };
    if (visible.value) {
      params.isShow = "1";
    }

    try {
      const res = await api.getH5ActivityDetail(params);
      if (res) {
        const { title: resTitle, background: resBackground, content: resContent } = res;
        background.value = resBackground;
        content.value = resContent;
        title.value = resTitle;
      } else {
        empty.value = true;
      }
    } catch (error) {
      console.error("Failed to load activity detail:", error);
      empty.value = true;
    }
  };

  // 绑定事件
  const bindEvents = (): void => {
    const contentEl = contentRef.value;
    if (!contentEl) return;

    const elems = contentEl.querySelectorAll("a");
    elems.forEach((el) => {
      el.addEventListener(
        "click",
        () => {
          handleLinkClick(el);
        },
        false,
      );
    });
  };

  const { $wx } = useNuxtApp();
  // 处理链接点击
  const handleLinkClick = (el: HTMLAnchorElement): void => {
    const { hash } = el;
    if (!hash) return;
    // 点击购买要立即跳转购买页，不能停留在首页才跳转  体验不好
    // if (hash === "#pay") {
    //   AppEvent.fire("click", { type: hash });
    //   try {
    //     $wx.miniProgram.postMessage({ data: { type: hash } });
    //     // 因为小程序postMessage不能马上接收到事件，所以这里先返回上一页强制触发事件
    //     // @ts-expect-error 声明文件缺少参数
    //     $wx.miniProgram.navigateBack();
    //   } catch (error) {
    //     console.error("WeChat miniprogram error:", error);
    //   }
    // }
    if (id.value === "09YPEvU") {
      Logger.event("得理法搜专业版下单加赠活动_跳转支付页面次数");
    }
    if (hash === "#pay") {
      $wx.miniProgram.navigateTo({ url: "/module/personal-center/buy-vip/index" });
    }
    if (hash.includes("#path")) {
      let path = hash.split("#path")[1];
      if (route.query.utm_source) {
        if (route.query.utm_source === "sms") Logger.event("推广活动_短信跳转支付页面次数");
        if (route.query.utm_source === "wechat_work") Logger.event("推广活动_企微跳转支付页面次数");
        if (route.query.utm_source === "wechat_mp") Logger.event("推广活动_公众号跳转支付页面次数");
        if (route.query.utm_source === "other") Logger.event("推广活动_其他内容平台跳转支付页面次数");
        path += `&utm_source=${route.query.utm_source}`;
        return $wx.miniProgram.navigateTo({ url: path as string });
      }
      $wx.miniProgram.navigateTo({ url: path as string });
      AppEvent.fire("navigateTo", { path });
    }
  };

  // 监听内容变化，重新绑定事件
  watch(content, () => {
    nextTick(() => {
      bindEvents();
    });
  });

  // 组件挂载
  onMounted(() => {
    const { id: routeId } = route.params;
    const { visible: routeVisible } = route.query;
    if (routeVisible) {
      visible.value = true;
    }
    if (routeId) {
      id.value = routeId as string;
      loadData();
      if (routeId === "09YPEvU") {
        Logger.event("得理法搜专业版下单加赠活动_进入次数");
      }
    }
  });
</script>

<template>
  <div class="tw-relative tw-min-h-[100vh]" :style="style">
    <div v-if="content" ref="contentRef" class="tw-ml-auto tw-mr-auto tw-max-w-[512px]" v-html="content"></div>
    <a-empty v-if="empty" class="tw-absolute-center" :image="noDataImageUrl">
      <template #description>
        <span>活动已结束</span>
      </template>
    </a-empty>
  </div>
</template>

<style lang="less">
  p {
    margin: 0;
  }

  img,
  video {
    max-width: 100%;
    margin-bottom: -1px;
  }
</style>
